
import Vue from 'vue';
import { Avatar } from '@run-diffusion/shared';
import { db } from '@/firebase';
import { get$bindFirestoreOptions } from '@/mixins';
import RunnitImage from '@/views/Runnits/RunnitImage.vue';

// One filter-dropdown option's avatar, rendered lazily and self-contained.
//
// Tools already carry a resolved `avatar` object (their node defs are
// vuexfire-bound), so pass that and it renders immediately. Boards come from the
// picker's raw union queries with only an `avatarId`, so pass that instead and
// this cell $binds the avatar doc itself — showing a skeleton while it loads —
// rather than the picker fetching every board's avatar up front. Mirrors
// TeamMemberTeamRoleTableCell: bind one doc on prop change, expose a loading
// flag for the skeleton, fall back to an icon when there's nothing to show.
export default Vue.extend({
	name: 'FilterOptionAvatar',
	components: {
		RunnitImage,
	},
	props: {
		// A resolved Avatar (tools) — rendered directly, no fetch.
		avatar: { type: Object as () => Avatar, default: null },
		// An avatar id (boards) — $bound on demand when no `avatar` is given.
		avatarId: { type: String, default: null },
		// Shown when there's no avatar to render (or it can't be read).
		fallbackIcon: { type: String, default: 'mdi-image-outline' },
		size: { type: Number, default: 24 },
	},
	data () {
		return {
			loading: false,
			boundAvatar: null as Avatar | null,
		};
	},
	computed: {
		resolvedAvatar (): Avatar | null {
			return this.avatar || this.boundAvatar;
		},
	},
	watch: {
		avatarId: {
			immediate: true,
			handler () {
				this.bindAvatar();
			},
		},
	},
	methods: {
		async bindAvatar () {
			// Already resolved (tools) or nothing to fetch → skeleton stays off.
			if (this.avatar || !this.avatarId) {
				this.boundAvatar = null;
				return;
			}
			this.loading = true;
			try {
				await this.$bind(
					'boundAvatar',
					db.collection('avatars').doc(this.avatarId),
					get$bindFirestoreOptions({ maxRefDepth: 0 }), // avatars carry no nested refs we need
				);
			} catch (e) {
				// Unreadable avatar → render the fallback icon instead.
				this.boundAvatar = null;
			} finally {
				this.loading = false;
			}
		},
	},
});
